// Path: ./src/app/pages/ElasticTrigger/engagement/EngagementIngestPanel.tsx
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryIcon from "@mui/icons-material/History";
import { AccountCardWrapper } from "../../../components/AccountCardWrapper";
import { StyledButton } from "../styled";
import { useEngagementIngest } from "./useEngagementIngest";
import { EngagementRunReport } from "./EngagementRunReport";
import { formatCount, formatRelative, formatTimestamp, EMPTY } from "./format";

const StatTile = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <Paper
    variant="outlined"
    sx={{ p: 1.5, minWidth: 140, flex: "1 1 140px", borderRadius: 2 }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Paper>
);

const Field = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box sx={{ minWidth: 200 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {children}
  </Box>
);

/**
 * Admin surface for the PostHog → Postgres engagement ingest that feeds the
 * Trending endpoints. Shows what the job is doing and lets a SuperAdmin verify
 * the pull before the schedule is switched on.
 *
 * The 12 `engagement.*` configuration keys are edited on the Configuration
 * Management page (category "Engagement"), not here.
 */
export const EngagementIngestPanel = () => {
  const {
    status,
    statusError,
    statusLoading,
    refreshStatus,
    running,
    lastOutcome,
    dismissOutcome,
    runIngest,
    runBackfill,
  } = useEngagementIngest();

  const busy = running !== null;
  const backfillRunning = running === "backfill";

  return (
    <AccountCardWrapper title="Engagement Ingest (PostHog → Trending)">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: 2,
          p: { xs: 2, sm: 3 },
        }}
      >
        {statusError && (
          <Alert severity="error">
            Could not load status: {statusError}
          </Alert>
        )}

        {statusLoading && !status ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          status && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Chip
                  size="small"
                  color={status.enabled ? "success" : "default"}
                  variant={status.enabled ? "filled" : "outlined"}
                  label={
                    status.enabled ? "Schedule enabled" : "Schedule disabled"
                  }
                />
                <Chip
                  size="small"
                  color={status.posthogConfigured ? "success" : "error"}
                  variant={status.posthogConfigured ? "filled" : "outlined"}
                  label={
                    status.posthogConfigured
                      ? "PostHog key configured"
                      : "PostHog key missing"
                  }
                />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Refresh status" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => refreshStatus()}
                      disabled={statusLoading}
                      aria-label="Refresh engagement ingest status"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {!status.posthogConfigured && (
                <Alert severity="error">
                  No PostHog API key is configured, so every pull will fail. Set
                  the key on the server before running the ingest.
                </Alert>
              )}

              {!status.enabled && status.posthogConfigured && (
                <Alert severity="info">
                  The scheduled job is off — it returns immediately on every
                  tick. The two buttons below ignore this switch on purpose, so
                  you can verify the pull first. Turn{" "}
                  <code>engagement.ingest.enabled</code> on from Configuration
                  Management when the numbers look right.
                </Alert>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                <Field label="Cron expression">
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {status.cronExpression || EMPTY}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    6 fields — seconds first
                  </Typography>
                </Field>
                <Field label="Next run">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {status.nextRunAt
                      ? formatTimestamp(status.nextRunAt)
                      : "Unscheduled"}
                  </Typography>
                  {status.nextRunAt && (
                    <Typography variant="caption" color="text.secondary">
                      {formatRelative(status.nextRunAt)}
                    </Typography>
                  )}
                </Field>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <StatTile
                  label="Bucket rows"
                  value={formatCount(status.totalRows)}
                />
                <StatTile
                  label="Distinct items"
                  value={formatCount(status.distinctItems)}
                />
                <StatTile
                  label="Total views"
                  value={formatCount(status.totalViews)}
                />
                <StatTile
                  label="Total clicks"
                  value={formatCount(status.totalClicks)}
                />
                <StatTile
                  label="Earliest bucket"
                  value={formatTimestamp(status.earliestBucket)}
                  hint={
                    status.earliestBucket
                      ? formatRelative(status.earliestBucket)
                      : undefined
                  }
                />
                <StatTile
                  label="Latest bucket"
                  value={formatTimestamp(status.latestBucket)}
                  hint={
                    status.latestBucket
                      ? formatRelative(status.latestBucket)
                      : "nothing stored yet"
                  }
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                These counts ignore deal visibility on purpose. They answer
                &ldquo;did the ingest write anything&rdquo;, not &ldquo;what will
                shoppers see&rdquo; — a table full of rows whose flyer lists have
                expired is a healthy ingest and an empty feed. A{" "}
                <strong>Latest bucket</strong> far behind now means the job is not
                running, or PostHog is returning nothing.
              </Typography>
            </>
          )
        )}

        <Divider />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Tooltip
            title="Pulls the overlap window (engagement.ingest.overlap_hours, default 8) from PostHog, upserts it, then prunes expired buckets. Runs even when the schedule is disabled."
            placement="top"
            arrow
          >
            <span>
              <StyledButton
                endIcon={
                  running === "ingest" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={runIngest}
                disabled={busy}
              >
                Run now
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip
            title="One-time pull of engagement.ingest.backfill_days (default 7) of history, one PostHog query per day. Synchronous and can take minutes. Safe to repeat — it overwrites rather than accumulates."
            placement="top"
            arrow
          >
            <span>
              <StyledButton
                endIcon={
                  backfillRunning ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <HistoryIcon />
                  )
                }
                onClick={runBackfill}
                disabled={busy}
              >
                Backfill history
              </StyledButton>
            </span>
          </Tooltip>
        </Box>

        {backfillRunning && (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            Backfill in progress. It runs one PostHog query per day of history
            and can take several minutes — keep this page open. If the gateway
            times out first, the run does <strong>not</strong> stop; status keeps
            refreshing and <strong>Earliest bucket</strong> will move backwards
            as days land.
          </Alert>
        )}

        {lastOutcome && (
          <EngagementRunReport
            outcome={lastOutcome}
            retryDisabled={busy}
            onRetry={
              lastOutcome.kind === "backfill" ? runBackfill : runIngest
            }
            onDismiss={dismissOutcome}
          />
        )}
      </Box>
    </AccountCardWrapper>
  );
};
